import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import React, { useEffect, useMemo, useRef } from 'react'
import { Platform, View } from 'react-native'
import { WebView } from 'react-native-webview'

// https://melibo.atlassian.net/servicedesk/customer/portal/2/topic/4ed1f9a5-3657-4e78-b454-346169842ff4/article/218988545
const getChatUrl = (key: string) =>
  `https://cdn.melibo.de/v2/app.html?key=${key}`

const getMeliboWidget = (key: string) => `
<melibo-widget class="w-full flex-1" mode="embed" key="${key}"></melibo-widget>
`

declare global {
  interface Window {
    melibo?: {
      openWidget: () => void
    }
  }
}

const useMeliboJs = () => {
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    if (typeof window?.melibo !== 'undefined') {
      window.melibo.openWidget()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cdn.melibo.de/v2/widget.js'
    script.async = true
    script.onload = () => {
      window.melibo?.openWidget()
    }
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])
}

const MeliboWeb = () => {
  useMeliboJs()
  const config = useRemoteConfig()
  const meliboWidget = useMemo(() => {
    if (!config?.region?.meliboChatKey) {
      return null
    }
    return getMeliboWidget(config?.region?.meliboChatKey)
  }, [config?.region?.meliboChatKey])

  if (!meliboWidget) {
    return null
  }

  return (
    <div
      className="relative flex w-full flex-1"
      dangerouslySetInnerHTML={{
        __html: meliboWidget,
      }}
    ></div>
  )
}

const MeliboWebview = () => {
  const webViewRef = useRef<WebView>(null)
  const config = useRemoteConfig()
  const chatUrl = useMemo(() => {
    if (!config?.region?.meliboChatKey) {
      return null
    }
    return getChatUrl(config?.region?.meliboChatKey)
  }, [config?.region?.meliboChatKey])

  if (!chatUrl) {
    return null
  }

  return (
    <WebView
      source={{ uri: chatUrl }}
      ref={webViewRef}
      onNavigationStateChange={(event) => {
        if (event.url !== chatUrl) {
          webViewRef.current?.stopLoading()
        }
      }}
      startInLoadingState={true}
    />
  )
}

const ChatWindow = () => {
  return (
    <View className="flex min-h-[700px] flex-1">
      {Platform.select({
        native: <MeliboWebview />,
        // default: <iframe className="grow" src={chatUrl} />,
        // We use the MeliboWeb component for web because it can be handled by the virtual keyboard outside of iframe
        default: <MeliboWeb />,
      })}
    </View>
  )
}

export default ChatWindow
